import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { log } from '../lib/logger'
import { gestorVisivel } from '../lib/filtroGestor'
import AnexoMes from '../components/AnexoMes'

const ANO_ATUAL = new Date().getFullYear()
const MES_ATUAL = new Date().getMonth() + 1
const MES_REF = MES_ATUAL === 1 ? 12 : MES_ATUAL - 1
const ANO_REF = MES_ATUAL === 1 ? ANO_ATUAL - 1 : ANO_ATUAL
const MESES = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez']

export default function Indicadores() {
  const { isAdmin, isGestor, profile, isSuperAdmin, selectedCompany, filtroGestor, setFiltroGestor } = useAuth()
  const navigate = useNavigate()
  const [indicadores, setIndicadores] = useState([])
  const [gestores, setGestores] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ nome: '', unidade: '', objetivo: 'maior', gestor_id: '', casas_decimais: 0, apenas_tatico: false })
  const [saving, setSaving] = useState(false)
  const [anoSel, setAnoSel] = useState(ANO_ATUAL)
  const [resultadosRef, setResultadosRef] = useState([])
  // Preferencia de exibicao dos taticos — persistida como o selectedCompany
  const [mostrarTaticos, setMostrarTaticos] = useState(
    () => localStorage.getItem('mostrarTaticos') === 'true'
  )

  useEffect(() => { localStorage.setItem('mostrarTaticos', String(mostrarTaticos)) }, [mostrarTaticos])

  // Filtro de responsavel compartilhado com as outras telas (ver AuthContext).
  // Coordenador nao tem o seletor, entao tambem nao herda o recorte.
  const podeFiltrarGestor = isAdmin || isSuperAdmin || isGestor
  const gestorSel = podeFiltrarGestor ? gestorVisivel(filtroGestor, gestores) : ''

  useEffect(() => { fetchAll() }, [selectedCompany])
  useEffect(() => { setIndicadores([]); fetchIndicadores() }, [gestorSel, selectedCompany])

  async function fetchAll() {
    setLoading(true)
    let profsQ = supabase.from('profiles').select('id,nome,cargo,role').order('nome')
    if (isSuperAdmin && selectedCompany) profsQ = profsQ.eq('company_id', selectedCompany.id)
    const [{ data: profs }, { data: res }] = await Promise.all([
      profsQ,
      supabase.from('resultados').select('indicador_id,meta,resultado').eq('ano', ANO_REF).eq('mes', MES_REF)
    ])
    setGestores(profs || [])
    setResultadosRef(res || [])
    await fetchIndicadores()
    setLoading(false)
  }

  async function fetchIndicadores() {
    let q = supabase.from('indicadores').select('*, profiles(nome,cargo)').order('nome')
    if (gestorSel) q = q.eq('gestor_id', gestorSel)
    if (isSuperAdmin && selectedCompany) q = q.eq('company_id', selectedCompany.id)
    const { data } = await q
    setIndicadores(data || [])
  }

  async function handleCreate(e) {
    e.preventDefault()
    setSaving(true)
    const payload = {
      nome: form.nome,
      unidade: form.unidade,
      objetivo: form.objetivo,
      casas_decimais: Number(form.casas_decimais),
      apenas_tatico: form.apenas_tatico,
      gestor_id: isAdminOrSuper ? (form.gestor_id || profile.id) : profile.id
    }
    // O trigger set_company_from_creator herda a empresa do criador, e o
    // super_admin nao tem empresa: sem mandar o company_id da empresa
    // selecionada, o indicador nasceria com NULL — invisivel no filtro da
    // propria empresa e, pior, para o proprio responsavel (ver v1.11).
    if (isSuperAdmin && selectedCompany) payload.company_id = selectedCompany.id
    await supabase.from('indicadores').insert(payload)
    setSaving(false)
    setShowForm(false)
    setForm({ nome: '', unidade: '', objetivo: 'maior', gestor_id: '', casas_decimais: 0, apenas_tatico: false })
    fetchAll()
  }

  async function handleDelete(id) {
    if (!window.confirm('Remover indicador e todos os seus dados?')) return
    await supabase.from('indicadores').delete().eq('id', id)
    fetchAll()
  }

  async function refreshResultadosRef() {
    const { data: res } = await supabase
      .from('resultados')
      .select('indicador_id,meta,resultado')
      .eq('ano', ANO_REF)
      .eq('mes', MES_REF)
    setResultadosRef(res || [])
  }

  // O trofeu e' um numero de desempenho: taticos ficam de fora.
  const estrategicos = indicadores.filter(i => !i.apenas_tatico)
  // Ja a listagem respeita o checkbox de exibicao.
  const indicadoresVisiveis = mostrarTaticos ? indicadores : estrategicos
  const qtdTaticos = indicadores.length - estrategicos.length

  const indComDadosRef = estrategicos.filter(ind => {
    const r = resultadosRef.find(x => x.indicador_id === ind.id)
    return r && r.resultado != null && r.meta != null
  })
  const todosOkRef = indComDadosRef.length > 0 && indComDadosRef.length === estrategicos.length && indComDadosRef.every(ind => {
    const r = resultadosRef.find(x => x.indicador_id === ind.id)
    return ind.objetivo === 'maior' ? r.resultado >= r.meta : r.resultado <= r.meta
  })
  const isAdminOrSuper = isAdmin || isSuperAdmin
  const nomeGestorTrofeu = gestorSel
    ? gestores.find(g => g.id === gestorSel)?.nome
    : !isAdminOrSuper ? profile?.nome : null
  const mostrarTrofeu = !loading && todosOkRef && (gestorSel || !isAdminOrSuper)

  return (
    <div>
      <div className="page-title">
        <div>
          <h2>Indicadores</h2>
          <p style={{ color: 'var(--text2)', fontSize: 13, marginTop: 2 }}>
            {isAdminOrSuper ? 'Todos os indicadores' : 'Seus indicadores'}
          </p>

        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {podeFiltrarGestor && gestores.length > 1 && (
            <select value={gestorSel} onChange={e => setFiltroGestor(e.target.value)} style={{ width: 'auto', minWidth: 160 }}>
              <option value="">Todos</option>
              {gestores.map(g => <option key={g.id} value={g.id}>{g.nome}</option>)}
            </select>
          )}
          {qtdTaticos > 0 && (
            <label style={{
              display: 'flex', alignItems: 'center', gap: 6, margin: 0,
              fontSize: 12, color: 'var(--text2)', cursor: 'pointer', whiteSpace: 'nowrap'
            }}>
              <input type="checkbox" checked={mostrarTaticos}
                onChange={e => setMostrarTaticos(e.target.checked)}
                style={{ width: 'auto', margin: 0, cursor: 'pointer' }} />
              Exibir táticos ({qtdTaticos})
            </label>
          )}
          <select value={anoSel} onChange={e => setAnoSel(Number(e.target.value))} style={{ width: 'auto' }}>
            {[ANO_ATUAL - 1, ANO_ATUAL, ANO_ATUAL + 1].map(a => <option key={a}>{a}</option>)}
          </select>
          {isAdminOrSuper && (
            <button className="btn btn-primary" onClick={() => setShowForm(s => !s)}>
              {showForm ? 'Cancelar' : '+ Novo indicador'}
            </button>
          )}

        </div>
      </div>

      {mostrarTrofeu && (
        <div className="trofeu-banner">
          <span className="trophy-icon">🏆</span>
          <div style={{ flex: 1, color: '#fff' }}>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.85, marginBottom: 2 }}>
              Troféu Mês Perfeito
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.2 }}>
              {nomeGestorTrofeu} bateu todas as {indComDadosRef.length} metas de {MESES[MES_REF - 1]}/{ANO_REF}!
            </div>
            <div style={{ fontSize: 12, opacity: 0.8, marginTop: 4 }}>
              100% de aderência — resultado extraordinário 🎯
            </div>
          </div>
          <span style={{ fontSize: 36, opacity: 0.6 }}>🎯</span>
        </div>
      )}

      {showForm && isAdminOrSuper && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ marginBottom: 16 }}>Novo indicador</h3>
          <form onSubmit={handleCreate}>
            <div className="row">
              <div className="col">
                <label>Nome do indicador</label>
                <input value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
                  placeholder="Ex: Satisfação do cliente" required />
              </div>
              <div className="col" style={{ maxWidth: 120 }}>
                <label>Unidade</label>
                <input value={form.unidade} onChange={e => setForm(f => ({ ...f, unidade: e.target.value }))}
                  placeholder="%, min, R$" />
              </div>
              <div className="col" style={{ maxWidth: 110 }}>
                <label>Casas decimais</label>
                <select value={form.casas_decimais} onChange={e => setForm(f => ({ ...f, casas_decimais: e.target.value }))}>
                  {[0, 1, 2, 3].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            </div>
            <div className="row" style={{ marginTop: 10 }}>
              <div className="col">
                <label>Objetivo</label>
                <select value={form.objetivo} onChange={e => setForm(f => ({ ...f, objetivo: e.target.value }))}>
                  <option value="maior">Maior é melhor (meta = mínimo)</option>
                  <option value="menor">Menor é melhor (meta = máximo)</option>
                </select>
              </div>
              <div className="col">
                <label>Gestor responsável</label>
                {/* Obrigatorio para o super_admin: o fallback `profile.id` o
                    colocaria como responsavel, e ele nao tem empresa — entao o
                    indicador nao teria de onde herdar o tenant. */}
                <select required={isSuperAdmin} value={form.gestor_id} onChange={e => setForm(f => ({ ...f, gestor_id: e.target.value }))}>
                  <option value="">Selecione...</option>
                  {gestores.map(g => <option key={g.id} value={g.id}>{g.nome}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Salvando...' : 'Criar'}
                </button>
              </div>
            </div>
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.apenas_tatico}
                  onChange={e => setForm(f => ({ ...f, apenas_tatico: e.target.checked }))}
                  style={{ width: 'auto', margin: 0, cursor: 'pointer' }} />
                <span style={{ fontSize: 13, fontWeight: 500 }}>Apenas nível tático</span>
              </label>
              <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 4, marginLeft: 24 }}>
                Fica fora da aderência, dos destaques e das contagens consolidadas.
                Continua sendo acompanhado normalmente e o fluxo de FCA não muda.
              </div>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="empty-state">Carregando...</div>
      ) : indicadoresVisiveis.length === 0 ? (
        <div className="empty-state">
          {indicadores.length > 0
            ? 'Todos os indicadores deste recorte são táticos. Marque “Exibir táticos” para vê-los.'
            : gestorSel ? 'Nenhum indicador para este responsável.'
            : isAdminOrSuper ? 'Nenhum indicador cadastrado.' : 'Nenhum indicador atribuído a você.'}
        </div>
      ) : indicadoresVisiveis.map(ind => (
        <IndicadorCard key={ind.id} ind={ind} ano={anoSel} isAdmin={isAdminOrSuper}
          onDelete={handleDelete} onNavigate={navigate} onSaved={refreshResultadosRef}
          onChangedNivel={fetchIndicadores} />
      ))}
    </div>
  )
}

function IndicadorCard({ ind, ano, isAdmin, onDelete, onNavigate, onSaved, onChangedNivel }) {
  const [open, setOpen] = useState(false)
  const [resultados, setResultados] = useState([])
  const [fcas, setFcas] = useState([])
  const [drafts, setDrafts] = useState({})
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [autoSaveStatus, setAutoSaveStatus] = useState(null) // null | 'saving' | 'saved'
  const [casas, setCasas] = useState(ind.casas_decimais ?? 0)
  const [tipoAcumulado, setTipoAcumulado] = useState(ind.tipo_acumulado ?? 'soma')
  const [apenasTatico, setApenasTatico] = useState(ind.apenas_tatico ?? false)
  const autoSaveTimer = useRef(null)

  async function handleCasasChange(val) {
    const n = Number(val)
    setCasas(n)
    await supabase.from('indicadores').update({ casas_decimais: n }).eq('id', ind.id)
  }

  async function handleApenasTaticoChange(val) {
    setApenasTatico(val)
    await supabase.from('indicadores').update({ apenas_tatico: val }).eq('id', ind.id)
    // Recarrega a lista: mudar o nivel altera trofeu, contagens e visibilidade.
    onChangedNivel?.()
  }

  async function handleTipoAcumuladoChange(val) {
    setTipoAcumulado(val)
    await supabase.from('indicadores').update({ tipo_acumulado: val }).eq('id', ind.id)
  }

  useEffect(() => { fetchResultados() }, [ind.id, ano])

  async function fetchResultados() {
    const [{ data }, { data: fcasData }] = await Promise.all([
      supabase.from('resultados').select('*').eq('indicador_id', ind.id).eq('ano', ano),
      supabase.from('fcas').select('id, mes, status').eq('indicador_id', ind.id).eq('ano', ano)
    ])
    const arr = Array.from({ length: 12 }, (_, i) => {
      const found = data?.find(r => r.mes === i + 1)
      return found || { indicador_id: ind.id, ano, mes: i + 1, meta: null, resultado: null }
    })
    setResultados(arr)
    setFcas(fcasData || [])
    setDrafts({})
  }

  function setDraft(mes, field, valor) {
    setDrafts(prev => ({ ...prev, [mes]: { ...(prev[mes] || {}), [field]: valor } }))
  }

  // O anexo grava na hora, fora do fluxo de drafts: enviar arquivo ja' e' uma
  // acao explicita, nao faria sentido ficar pendente de "Salvar".
  async function salvarAnexo(mes, campos) {
    const { data, error } = await supabase
      .from('resultados')
      .upsert({ indicador_id: ind.id, ano, mes, ...campos }, { onConflict: 'indicador_id,ano,mes' })
      .select()
    if (error) return { error: error.message }
    setResultados(prev => prev.map(r => (r.mes === mes ? (data?.[0] ?? { ...r, ...campos }) : r)))
    return {}
  }

  function getDisplay(r, field) {
    const d = drafts[r.mes]
    if (d && field in d) return d[field]
    return r[field] ?? ''
  }

  async function doSave(currentDrafts, { autoSave = false } = {}) {
    if (Object.keys(currentDrafts).length === 0) return
    setSaving(true)
    setSaveError(null)
    const novosResultados = resultados.map(r => ({ ...r }))
    let hasError = false

    for (const [mes, draft] of Object.entries(currentDrafts)) {
      const idx = novosResultados.findIndex(r => r.mes === Number(mes))
      const row = novosResultados[idx]
      const meta = 'meta' in draft ? (draft.meta === '' ? null : Number(draft.meta)) : row.meta
      const resultado = 'resultado' in draft ? (draft.resultado === '' ? null : Number(draft.resultado)) : row.resultado
      const acumuladoVal = tipoAcumulado === 'atualizavel'
        ? ('acumulado' in draft ? (draft.acumulado === '' ? null : Number(draft.acumulado)) : row.acumulado)
        : undefined

      const payload = { indicador_id: ind.id, ano, mes: Number(mes), meta, resultado }
      if (tipoAcumulado === 'atualizavel') payload.acumulado = acumuladoVal ?? null

      const { data: rows, error } = await supabase
        .from('resultados')
        .upsert(payload, { onConflict: 'indicador_id,ano,mes' })
        .select()

      if (error) {
        console.error('Erro ao salvar mes', mes, error)
        hasError = true
        continue
      }

      novosResultados[idx] = rows?.[0] ?? { ...row, meta, resultado }
    }

    setResultados(novosResultados)
    setSaving(false)

    if (hasError) {
      setSaveError('Erro ao salvar. Verifique o console para detalhes.')
    } else {
      log('resultado_salvo', 'indicador', ind.id, {
        indicador: ind.nome,
        ano,
        meses: Object.keys(currentDrafts).map(Number),
      })
      setDrafts({})
      onSaved?.()
      if (autoSave) {
        setAutoSaveStatus('saved')
        setTimeout(() => setAutoSaveStatus(null), 3000)
      } else {
        setOpen(false)
        setSaved(true)
        setTimeout(() => setSaved(false), 2500)
      }
    }
  }

  async function handleSave() {
    await doSave(drafts, { autoSave: false })
  }

  // Auto-save com debounce de 2s
  useEffect(() => {
    if (!open || Object.keys(drafts).length === 0) return
    const snapshot = { ...drafts }
    clearTimeout(autoSaveTimer.current)
    autoSaveTimer.current = setTimeout(() => doSave(snapshot, { autoSave: true }), 2000)
    return () => clearTimeout(autoSaveTimer.current)
  }, [drafts, open])

  function handleFca(mes) {
    onNavigate(`/fcas?indicador=${ind.id}&mes=${mes}`)
  }

  function getStatus(r) {
    const d = drafts[r.mes]
    const meta = d && 'meta' in d ? (d.meta === '' ? null : Number(d.meta)) : r.meta
    const resultado = d && 'resultado' in d ? (d.resultado === '' ? null : Number(d.resultado)) : r.resultado
    if (resultado === null || meta === null || meta === 0) return null
    return ind.objetivo === 'maior' ? resultado >= meta : resultado <= meta
  }

  function getSavedStatus(r) {
    if (r.resultado == null || r.meta == null || r.meta === 0) return null
    return ind.objetivo === 'maior' ? r.resultado >= r.meta : r.resultado <= r.meta
  }

  const comDados = resultados.filter(r => r.resultado !== null)
  const atingidos = comDados.filter(r => {
    if (r.meta === null || r.meta === 0) return false
    return ind.objetivo === 'maior' ? r.resultado >= r.meta : r.resultado <= r.meta
  }).length
  const nok = comDados.length - atingidos
  const hasEdits = Object.keys(drafts).length > 0

  return (
    <div className="card" style={{ marginBottom: 12, padding: 0 }}>
      {/* Cabeçalho — clicável para abrir/fechar */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '12px 16px', cursor: 'pointer', userSelect: 'none',
          borderRadius: open ? '10px 10px 0 0' : 10,
          background: open ? 'var(--surface)' : 'var(--surface2)',
          transition: 'background 0.15s',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <span style={{
            fontSize: 13, color: 'var(--text3)', flexShrink: 0,
            transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s', display: 'inline-block'
          }}>▶</span>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 600, fontSize: 15 }}>{ind.nome}</span>
              {ind.unidade && <span className="badge badge-gray">{ind.unidade}</span>}
              <span className="badge badge-gray">{ind.objetivo === 'maior' ? '↑ maior melhor' : '↓ menor melhor'}</span>
              {apenasTatico && <span className="badge badge-amber">tático</span>}
              {comDados.length > 0 && (
                <>
                  <span className="badge badge-green">{atingidos} ok</span>
                  {nok > 0 && <span className="badge badge-red">{nok} nok</span>}
                </>
              )}
              {hasEdits && <span className="badge badge-amber">não salvo</span>}
              {saved && <span className="badge badge-green">Salvo ✓</span>}
            </div>
            {!open && (
              <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>
                {ind.profiles?.nome} · {ind.profiles?.cargo}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0, marginLeft: 12 }}
          onClick={e => e.stopPropagation()}>
          <button className="btn btn-sm" onClick={() => onNavigate(`/dashboard/${ind.id}`)}>
            Ver gráfico
          </button>
          {isAdmin && (
            <button className="btn btn-sm btn-danger" onClick={() => onDelete(ind.id)}>
              Remover
            </button>
          )}
        </div>
      </div>

      {open && (
        <div style={{ padding: '14px 16px' }}>
          {/* METAS — editável só pelo admin, somente leitura para gestor */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text2)' }}>Metas — {ano}</span>
              {!isAdmin && <span className="badge badge-blue" style={{ fontSize: 10 }}>somente leitura</span>}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 4 }}>
              {resultados.map((r, i) => (
                <div key={i}>
                  <div style={{ fontSize: 10, color: 'var(--text3)', textAlign: 'center', marginBottom: 2 }}>{MESES[i]}</div>
                  {isAdmin ? (
                    <input
                      type="number" step="0.01"
                      value={getDisplay(r, 'meta')}
                      placeholder="—"
                      onChange={e => setDraft(r.mes, 'meta', e.target.value)}
                      style={{ textAlign: 'center', padding: '4px 2px', fontSize: 12 }}
                    />
                  ) : (
                    <div style={{
                      textAlign: 'center', padding: '5px 2px', fontSize: 12,
                      background: 'var(--surface2)', border: '1px solid var(--border)',
                      borderRadius: 6, color: r.meta !== null ? 'var(--text)' : 'var(--text3)',
                      minHeight: 30, display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      {r.meta !== null ? Number(r.meta).toFixed(casas) : '—'}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* ACUMULADO ATUALIZÁVEL — editável por todos */}
          {tipoAcumulado === 'atualizavel' && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text2)', marginBottom: 6 }}>Acumulado — {ano}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 4 }}>
                {resultados.map((r, i) => (
                  <div key={i}>
                    <div style={{ fontSize: 10, color: 'var(--text3)', textAlign: 'center', marginBottom: 2 }}>{MESES[i]}</div>
                    <input
                      type="number" step="0.01"
                      value={drafts[r.mes] && 'acumulado' in drafts[r.mes] ? drafts[r.mes].acumulado : (r.acumulado ?? '')}
                      placeholder="—"
                      onChange={e => setDraft(r.mes, 'acumulado', e.target.value)}
                      style={{ textAlign: 'center', padding: '4px 2px', fontSize: 12 }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* RESULTADOS — editável por todos */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text2)', marginBottom: 6 }}>Resultados — {ano}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 4 }}>
              {resultados.map((r, i) => {
                const status = getStatus(r)
                const savedNok = getSavedStatus(r) === false
                const fca = fcas.find(f => f.mes === r.mes)
                return (
                  <div key={i}>
                    <div style={{ fontSize: 10, color: 'var(--text3)', textAlign: 'center', marginBottom: 2 }}>{MESES[i]}</div>
                    <input
                      type="number" step="0.01"
                      value={getDisplay(r, 'resultado')}
                      placeholder="—"
                      onChange={e => setDraft(r.mes, 'resultado', e.target.value)}
                      style={{
                        textAlign: 'center', padding: '4px 2px', fontSize: 12,
                        borderColor: status === false ? 'var(--red-border)' : status === true ? 'var(--green-border)' : undefined,
                        background: status === false ? 'var(--red-bg)' : status === true ? 'var(--green-bg)' : undefined,
                      }}
                    />
                    {savedNok && (
                      <button
                        onClick={() => handleFca(r.mes)}
                        style={{
                          marginTop: 3, width: '100%', fontSize: 10, padding: '2px 0',
                          background: fca ? 'var(--amber-bg)' : 'var(--red-bg)',
                          color: fca ? 'var(--amber)' : 'var(--red)',
                          border: `1px solid ${fca ? 'var(--amber-border)' : 'var(--red-border)'}`,
                          borderRadius: 4, cursor: 'pointer', textAlign: 'center',
                          fontFamily: 'var(--font)', fontWeight: 500,
                        }}
                      >
                        {fca ? 'Ver FCA' : '+ FCA'}
                      </button>
                    )}
                    <AnexoMes
                      resultado={r}
                      mesAnterior={resultados[i - 1]}
                      indicador={ind}
                      ano={ano}
                      podeEditar
                      onSalvar={salvarAnexo}
                    />
                  </div>
                )
              })}
            </div>
          </div>

          {/* Rodapé salvar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
            {isAdmin && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <label style={{ margin: 0, fontSize: 12, color: 'var(--text2)' }}>Casas decimais</label>
                  <select value={casas} onChange={e => handleCasasChange(e.target.value)} style={{ width: 'auto', padding: '3px 6px', fontSize: 12 }}>
                    {[0, 1, 2, 3].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <label style={{ margin: 0, fontSize: 12, color: 'var(--text2)' }}>Acumulado</label>
                  <select value={tipoAcumulado} onChange={e => handleTipoAcumuladoChange(e.target.value)} style={{ width: 'auto', padding: '3px 6px', fontSize: 12 }}>
                    <option value="soma">Soma</option>
                    <option value="media">Média</option>
                    <option value="atualizavel">Atualizável</option>
                  </select>
                </div>
                <label style={{
                  display: 'flex', alignItems: 'center', gap: 6, margin: 0,
                  fontSize: 12, color: 'var(--text2)', cursor: 'pointer'
                }} title="Fica fora da aderência, dos destaques e das contagens consolidadas. O fluxo de FCA não muda.">
                  <input type="checkbox" checked={apenasTatico}
                    onChange={e => handleApenasTaticoChange(e.target.checked)}
                    style={{ width: 'auto', margin: 0, cursor: 'pointer' }} />
                  Apenas nível tático
                </label>
              </div>
            )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {saved && <span style={{ fontSize: 12, color: 'var(--green)', fontWeight: 500 }}>Salvo com sucesso ✓</span>}
            {saveError && <span style={{ fontSize: 12, color: 'var(--red)' }}>{saveError}</span>}
            {autoSaveStatus === 'saving' && <span style={{ fontSize: 12, color: 'var(--text3)' }}>💾 Salvando...</span>}
            {autoSaveStatus === 'saved' && <span style={{ fontSize: 12, color: 'var(--text3)' }}>💾 Salvo automaticamente às {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>}
            {hasEdits && !saving && !autoSaveStatus && <span style={{ fontSize: 12, color: 'var(--text3)' }}>Salvando em instantes...</span>}
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving || !hasEdits}
              style={{ minWidth: 100, opacity: hasEdits ? 1 : 0.45 }}
            >
              {saving ? 'Salvando...' : 'Salvar e fechar'}
            </button>
          </div>
          </div>
        </div>
      )}
    </div>
  )
}
